import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import ContactContext from 'contexts/contactContext'
import styled from 'styled-components'
import { rem } from 'polished'
import Image from 'components/Common/Image'
import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const SupportImageContainer = styled.div`
  height: ${rem(168)};
  overflow: hidden;

  margin-inline: -${rem(20)};

  ${mediaQueryUp.tablet} {
    height: ${rem(280)};
    margin-inline: -${rem(24)};
  }
`

const SupportImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const IMAGE_ID = 'f2cbkfa3neeqk0suw16u'

function FlightSearchMaxFlightsModal() {
  const modalElement = useModalElementContext<boolean>()
  const currentContact = useContext(ContactContext)
  const contact = currentContact.defaultContact
  const intl = useIntl()

  const title = useMemo(
    () =>
      intl.formatMessage({
        id: 'q-q4GzuzVw9Qk0sAbpfl',
        defaultMessage: 'Want to book more than 4 routes?',
        description: 'Title for modal shown when user tries to book more than 4 flight routes',
      }),
    [intl],
  )

  const primaryActionText = useMemo(
    () =>
      intl.formatMessage(
        {
          id: 'nQx-syjWLEUqvYSw-1JG',
          defaultMessage: 'Call {phoneNumber}',
          description: 'Button text to call support for booking more than 4 flight routes',
        },
        { phoneNumber: contact.humanReadable },
      ),
    [contact.humanReadable, intl],
  )

  const bodyText = useMemo(
    () =>
      intl.formatMessage({
        id: 'jRnIoO-ver6Rb-NlsoyE',
        defaultMessage: 'Our concierge team are available 24 hours, 7 days to assist you. Call us to book more routes.',
        description: 'Body text explaining concierge availability for booking more than 4 flight routes',
      }),
    [intl],
  )

  const sayYes = () => {
    modalElement.resolve(true)
  }

  const sayNo = () => {
    modalElement.resolve(false)
  }

  return (
    <Modal
      title={title}
      primaryActionText={primaryActionText}
      primaryActionHref={`tel:${contact.number}`}
      onPrimaryActionClick={sayYes}
      onSecondaryActionClick={sayNo}
    >

      <VerticalSpacer gap={20}>
        <SupportImageContainer>
          <SupportImage
            fit="center"
            id={IMAGE_ID}
            alt={intl.formatMessage({
              id: 'Y4KiOLI2F4o1gLsYuKx0',
              defaultMessage: 'Support',
              description: 'Alt text for the support illustration in the max flights modal',
            })}
            width={560}
            dpr={2}
          />
        </SupportImageContainer>

        <BodyTextBlock variant="large">{bodyText}</BodyTextBlock>
      </VerticalSpacer>
    </Modal>
  )
}

export default FlightSearchMaxFlightsModal
